import { CheckCircle, InfoIcon } from '@blueprinthq/joy'
import {
  Box,
  Center,
  HStack,
  Image,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack
} from '@chakra-ui/react'
import React from 'react'

interface TierItem {
  tier: number
  rewardText: string
  giftHoverText?: string
  applied: boolean
  earned: boolean
}

interface RewardTiersTabProps {
  tierStatusData:
    | {
        tiers: TierItem[]
      }
    | undefined
  isLoadingTierStatus: boolean
}

export const RewardTiersTab: React.FC<RewardTiersTabProps> = ({
  tierStatusData,
  isLoadingTierStatus
}) => {
  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {isLoadingTierStatus ? (
          <Center p={8} data-testid="tier-status-loading">
            <Spinner size="lg" />
          </Center>
        ) : (
          <Box data-testid="tier-status-content">
            <Box
              overflowY="auto"
              maxHeight="476px"
              data-testid="tier-status-table-container"
            >
              <Table variant="simple" data-testid="tier-status-table">
                <Thead top={0} bg="white" zIndex={1}>
                  <Tr borderBottom="1px" borderColor="gray.200">
                    <Th
                      py={4}
                      pl={6}
                      color="#282828"
                      fontWeight="bold"
                      maxWidth="120px"
                      textTransform="none"
                    >
                      Refer to Paid
                    </Th>
                    <Th
                      py={4}
                      color="#282828"
                      fontWeight="bold"
                      maxWidth="220px"
                      textTransform="none"
                    >
                      Reward{' '}
                      <Text
                        as="span"
                        color="#A1A1A1"
                        fontWeight="normal"
                        fontSize="xs"
                      >
                        (Cumulative)
                      </Text>
                    </Th>
                    <Th
                      py={4}
                      pr={6}
                      color="#282828"
                      fontWeight="bold"
                      textTransform="none"
                    >
                      Status
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tierStatusData?.tiers.map(tier => (
                    <Tr
                      height="40px"
                      key={tier.tier}
                      borderBottom="1px"
                      borderColor="gray.200"
                      bg={tier.applied || tier.earned ? '#F6F7FE' : undefined}
                    >
                      <Td pl={6} fontWeight="medium" maxWidth="120px">
                        {tier.tier}
                      </Td>
                      <Td maxWidth="220px" pr="0">
                        {tier.tier === 10 ? (
                          <HStack>
                            <Text as="span" fontSize="lg">
                              🎉
                            </Text>
                            <Text as="span" fontWeight="bold">
                              FREE for LIFE!
                            </Text>
                          </HStack>
                        ) : (
                          <HStack spacing={1} align="center">
                            <Text as="span">{tier.rewardText}</Text>
                            {tier.giftHoverText && (
                              <Tooltip
                                label={tier.giftHoverText}
                                maxWidth="175px"
                                hasArrow
                                placement="top"
                                borderRadius="2px"
                                lineHeight="1.3"
                                py="8px"
                              >
                                <Box as="span" display="inline-flex">
                                  <InfoIcon boxSize="14px" fill="#A1A1A1" />
                                </Box>
                              </Tooltip>
                            )}
                          </HStack>
                        )}
                      </Td>
                      <Td pr={6}>
                        {tier.applied ? (
                          <HStack>
                            <CheckCircle
                              fill="primary"
                              width="16px"
                              height="16px"
                            />
                            <Text>Reward applied</Text>
                          </HStack>
                        ) : tier.earned ? (
                          <HStack>
                            <Image
                              src="https://res.cloudinary.com/hellojoy/image/upload/v1745440287/Wait_Filled_k1wjrg.svg"
                              alt="Waiting for reward"
                              width="16px"
                              height="16px"
                            />
                            <Text fontWeight="medium" ml="0">
                              Earned
                            </Text>
                            <Text as="span" color="#A1A1A1" ml="4px !important">
                              (in queue)
                            </Text>
                          </HStack>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <Box
                borderRadius="8px"
                border="1px solid rgba(40, 40, 40, 0.05)"
                bg="#F6F7FE"
                padding="12px"
                mx="24px"
                my="24px"
              >
                <Text>
                  Want to keep earning? Join our{' '}
                  <Link
                    href="mailto:marketing@blueprint-health.com"
                    color="primary"
                  >
                    Affiliate Program
                  </Link>
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default RewardTiersTab
